/**
 * Bare-Metal Checkout Page
 * Handles payment processing with wallet balance and payment gateway options
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  WalletIcon,
  CreditCardIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowLeftIcon,
  XMarkIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import Header from '../../components/layout/Header';
import { baremetalApi } from '../../services/baremetalApi';
import { walletService, currencyService } from '../../services/api';
import { loadRazorpayScript } from '../../utils/razorpay';

const Checkout: React.FC = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const selectedProjectId = localStorage.getItem('selectedProjectId') || '';

  // State
  const [useWalletBalance, setUseWalletBalance] = useState(true);
  const [walletAmount, setWalletAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<'razorpay' | 'paypal' | 'binance' | 'wallet_only'>('wallet_only');
  const [paymentRegion, setPaymentRegion] = useState<'india' | 'overseas' | 'crypto'>('india');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Feature flags for payment methods
  const isPayPalEnabled = process.env.REACT_APP_ENABLE_PAYPAL === 'true';
  const isCryptoEnabled = process.env.REACT_APP_ENABLE_CRYPTO === 'true';

  // Fetch order details
  const { data: orderStatus, isLoading: isLoadingOrder } = useQuery({
    queryKey: ['baremetal-order-status', orderId],
    queryFn: () => baremetalApi.getOrderStatus(orderId!, selectedProjectId),
    enabled: !!orderId,
  });

  // Fetch wallet balance
  const { data: walletData } = useQuery({
    queryKey: ['wallet'],
    queryFn: () => walletService.get().then((res) => res.data),
  });

  // Fetch currency rates
  const { data: currencyData } = useQuery({
    queryKey: ['currency-rates'],
    queryFn: () => currencyService.get().then((res) => res.data),
  });

  const order = orderStatus?.data;
  const walletBalance = walletData?.balance || 0;
  const exchangeRate = currencyData?.usd_to_inr || 92;

  // Calculate amounts
  const totalAmount = order?.total_price_inr || 0;
  const maxWalletAmount = Math.min(walletBalance, totalAmount);
  const amountToPay = useWalletBalance ? totalAmount - walletAmount : totalAmount;
  const canPayWithWalletOnly = walletBalance >= totalAmount;

  // Auto-set wallet amount based on balance
  useEffect(() => {
    if (canPayWithWalletOnly && useWalletBalance) {
      setWalletAmount(totalAmount);
    } else if (useWalletBalance && maxWalletAmount > 0) {
      setWalletAmount(maxWalletAmount);
    }
  }, [canPayWithWalletOnly, maxWalletAmount, totalAmount, useWalletBalance]);

  // Update payment method based on region selection in modal
  useEffect(() => {
    if (paymentRegion === 'india') {
      setPaymentMethod('razorpay');
    } else if (paymentRegion === 'overseas') {
      setPaymentMethod('paypal');
    } else if (paymentRegion === 'crypto') {
      setPaymentMethod('binance');
    }
  }, [paymentRegion]);

  // Checkout mutation
  const checkoutMutation = useMutation({
    mutationFn: () => baremetalApi.checkout(orderId!, selectedProjectId, {
      use_wallet_balance: useWalletBalance,
      wallet_amount: useWalletBalance ? walletAmount : undefined,
      payment_method: paymentMethod,
    }),
    onSuccess: (response) => {
      const checkoutData = response.data;

      // If fully paid with wallet
      if (checkoutData.amount_to_pay === 0) {
        toast.success('Order confirmed! Your wallet balance has been deducted.');
        navigate(`/portal/baremetal/status/${orderId}`);
        return;
      }

      // Handle payment gateways
      if (paymentMethod === 'razorpay') {
        openRazorpay(checkoutData);
      } else if (paymentMethod === 'paypal') {
        if (checkoutData.paypal_approval_url) {
          window.location.href = checkoutData.paypal_approval_url;
        }
      } else if (paymentMethod === 'binance') {
        if (checkoutData.binance_checkout_url) {
          window.location.href = checkoutData.binance_checkout_url;
        }
      }
    },
    onError: (error: any) => {
      setIsProcessing(false);
      toast.error(error.response?.data?.error || 'Checkout failed');
    },
  });

  const openRazorpay = async (checkoutData: any) => {
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      setIsProcessing(false);
      toast.error('Failed to load payment gateway. Please try again.');
      return;
    }

    const options = {
      key: checkoutData.razorpay_key,
      amount: checkoutData.amount_to_pay * 100, // Convert to paise
      currency: 'INR',
      name: 'Podstack',
      description: `Bare-Metal Server Order ${checkoutData.order_id}`,
      order_id: checkoutData.razorpay_order_id,
      handler: async () => {
        toast.success('Payment successful! Processing your order...');
        navigate(`/portal/baremetal/status/${orderId}`);
      },
      modal: {
        ondismiss: () => {
          setIsProcessing(false);
          toast.error('Payment cancelled');
        },
      },
      theme: {
        color: '#F97316',
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  const handleCheckout = () => {
    if (!orderId || !selectedProjectId) {
      toast.error('Invalid order or project');
      return;
    }

    if (useWalletBalance && walletAmount > maxWalletAmount) {
      toast.error(`Maximum wallet amount is ₹${maxWalletAmount.toFixed(2)}`);
      return;
    }

    // If can pay with wallet only and amount is 0, process immediately
    if (amountToPay === 0 && canPayWithWalletOnly && useWalletBalance) {
      setPaymentMethod('wallet_only');
      setIsProcessing(true);
      checkoutMutation.mutate();
      return;
    }

    // Show payment modal for gateway selection (even if using wallet balance)
    setShowPaymentModal(true);
  };

  const handlePaymentConfirm = () => {
    if (amountToPay > 0 && paymentMethod === 'wallet_only') {
      toast.error('Insufficient wallet balance. Please select a payment method.');
      return;
    }

    setShowPaymentModal(false);
    setIsProcessing(true);
    checkoutMutation.mutate();
  };

  if (isLoadingOrder) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-gray-700 border-t-primary-500 rounded-full animate-spin mb-4 mx-auto"></div>
          <p className="text-sm text-gray-400">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-white">Order not found</p>
          <button
            onClick={() => navigate('/portal/baremetal/create')}
            className="mt-4 text-primary-500 hover:text-primary-400"
          >
            Create New Order
          </button>
        </div>
      </div>
    );
  }

  // Check if order is already paid
  if (order.status === 'paid' || order.status === 'provisioning' || order.status === 'active') {
    navigate(`/portal/baremetal/status/${orderId}`);
    return null;
  }

  return (
    <div>
      <Header
        title="Checkout"
        subtitle="Complete your payment for the bare-metal server order"
      />

      <div className="p-6 max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <button
          onClick={() => navigate('/portal/baremetal/create')}
          className="flex items-center gap-2 text-gray-400 hover:text-white text-sm"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Order Form
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Payment Options */}
          <div className="lg:col-span-2 space-y-6">
            {/* Wallet Balance Section */}
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <WalletIcon className="w-6 h-6 text-primary-500" />
                  <h3 className="text-lg font-semibold text-white">Wallet Balance</h3>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-white">₹{walletBalance.toFixed(2)}</p>
                  <p className="text-xs text-gray-400">Available Balance</p>
                </div>
              </div>

              {/* Use Wallet Toggle */}
              <label className="flex items-center gap-3 cursor-pointer p-3 bg-gray-800 rounded-lg hover:bg-gray-750 transition-colors">
                <input
                  type="checkbox"
                  checked={useWalletBalance}
                  onChange={(e) => {
                    setUseWalletBalance(e.target.checked);
                    if (!e.target.checked) {
                      setWalletAmount(0);
                      setPaymentMethod('razorpay');
                    } else {
                      setWalletAmount(maxWalletAmount);
                    }
                  }}
                  className="w-4 h-4 rounded border-gray-600 text-primary-500 focus:ring-primary-500 bg-gray-800"
                />
                <span className="text-sm text-gray-300">Use wallet balance for this order</span>
              </label>

              {/* Wallet Amount Slider */}
              {useWalletBalance && maxWalletAmount > 0 && (
                <div className="mt-4 p-4 bg-gray-800 rounded-lg">
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-gray-300">Amount to use from wallet:</label>
                    <span className="text-lg font-bold text-primary-400">₹{walletAmount.toFixed(2)}</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max={maxWalletAmount}
                    step="100"
                    value={walletAmount}
                    onChange={(e) => setWalletAmount(Number(e.target.value))}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>₹0</span>
                    <span>₹{maxWalletAmount.toFixed(2)}</span>
                  </div>
                </div>
              )}

              {canPayWithWalletOnly && (
                <div className="mt-4 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-2">
                  <CheckCircleIcon className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-green-400">Sufficient Balance</p>
                    <p className="text-xs text-gray-400">You can complete this order using wallet balance only.</p>
                  </div>
                </div>
              )}
            </div>

            {/* Payment Info */}
            {amountToPay > 0 && (
              <div className="bg-gray-900 rounded-xl border border-gray-700 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <CreditCardIcon className="w-6 h-6 text-primary-500" />
                  <h3 className="text-lg font-semibold text-white">Payment</h3>
                </div>

                <p className="text-sm text-gray-400">
                  When you click "Proceed to Checkout", you'll be able to choose from multiple payment options including Razorpay, PayPal, and Crypto.
                </p>

                <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                  <p className="text-sm text-blue-400">
                    Amount to pay: <span className="font-semibold">₹{amountToPay.toFixed(2)}</span>
                    {' '}(≈ ${(amountToPay / exchangeRate).toFixed(2)} USD)
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-gray-900 rounded-xl border border-gray-700 p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-white mb-4">Order Summary</h3>

              <div className="space-y-3 mb-4 pb-4 border-b border-gray-700">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Order ID:</span>
                  <span className="text-white font-mono">{order.order_id}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">CPU:</span>
                  <span className="text-white">{order.cpu} Cores</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">RAM:</span>
                  <span className="text-white">{order.ram} GB</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Storage:</span>
                  <span className="text-white">{order.storage} GB {order.storage_type}</span>
                </div>
                {order.gpu && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">GPU:</span>
                    <span className="text-white">{order.gpu_count}x {order.gpu}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Duration:</span>
                  <span className="text-white">{order.duration} month{order.duration > 1 ? 's' : ''}</span>
                </div>
              </div>

              <div className="space-y-2 mb-4 pb-4 border-b border-gray-700">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Total Amount:</span>
                  <span className="text-white">₹{totalAmount.toFixed(2)}</span>
                </div>
                {useWalletBalance && walletAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-green-400">Wallet Deduction:</span>
                    <span className="text-green-400">-₹{walletAmount.toFixed(2)}</span>
                  </div>
                )}
              </div>

              <div className="flex justify-between items-center mb-6">
                <span className="text-gray-300 font-medium">Amount to Pay:</span>
                <span className="text-2xl font-bold text-primary-400">
                  ₹{amountToPay.toFixed(2)}
                </span>
              </div>

              <button
                onClick={handleCheckout}
                disabled={isProcessing || checkoutMutation.isPending}
                className="w-full py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg text-white font-medium transition-colors"
              >
                {isProcessing || checkoutMutation.isPending ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Processing...
                  </span>
                ) : amountToPay === 0 ? (
                  'Confirm Order'
                ) : (
                  'Proceed to Checkout'
                )}
              </button>

              <p className="text-xs text-gray-500 text-center mt-4">
                Your server will be manually provisioned by our team within 24-48 hours after payment confirmation.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Method Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-800 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
              <h3 className="text-lg font-semibold text-white">
                Select Payment Method
              </h3>
              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setPaymentRegion('india');
                }}
                className="p-2 text-gray-400 hover:text-gray-300 rounded-lg"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto flex-1">
              {/* Payment Region Tabs */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select Payment Region
                </label>
                <div className="flex rounded-lg bg-gray-800 p-1">
                  <button
                    onClick={() => setPaymentRegion('india')}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      paymentRegion === 'india'
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    India
                  </button>
                  <button
                    onClick={() => setPaymentRegion('overseas')}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      paymentRegion === 'overseas'
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Overseas
                  </button>
                  <button
                    onClick={() => setPaymentRegion('crypto')}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      paymentRegion === 'crypto'
                        ? 'bg-primary-500 text-white'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    Crypto
                  </button>
                </div>
              </div>

              {/* India - Razorpay */}
              {paymentRegion === 'india' && (
                <>
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
                    <p className="text-sm text-blue-400">
                      Pay securely via Razorpay. Supports UPI, Cards, Net Banking, and Wallets.
                    </p>
                  </div>
                  <div className="mb-4 p-4 bg-gray-800 rounded-lg">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-gray-400">Amount to Pay:</span>
                      <span className="text-xl font-bold text-white">₹{amountToPay.toFixed(2)}</span>
                    </div>
                    {useWalletBalance && walletAmount > 0 && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-green-400">Wallet Deduction:</span>
                        <span className="text-green-400">-₹{walletAmount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={handlePaymentConfirm}
                    disabled={isProcessing}
                    className="btn-primary w-full py-3 text-base disabled:opacity-50"
                  >
                    {isProcessing ? 'Processing...' : `Pay with Razorpay - ₹${amountToPay.toFixed(2)}`}
                  </button>
                  <p className="text-xs text-gray-500 text-center mt-3">
                    Secure payment powered by Razorpay
                  </p>
                </>
              )}

              {/* Overseas - PayPal */}
              {paymentRegion === 'overseas' && (
                <>
                  {isPayPalEnabled ? (
                    <>
                      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 mb-4">
                        <p className="text-sm text-blue-400">
                          Pay securely via PayPal. Supports credit cards, debit cards, and PayPal balance.
                        </p>
                      </div>
                      <div className="mb-4 p-4 bg-gray-800 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-400">Amount to Pay:</span>
                          <span className="text-xl font-bold text-white">${(amountToPay / exchangeRate).toFixed(2)} USD</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Equivalent to:</span>
                          <span className="text-gray-400">₹{amountToPay.toFixed(2)}</span>
                        </div>
                        {useWalletBalance && walletAmount > 0 && (
                          <div className="flex justify-between items-center text-sm mt-1">
                            <span className="text-green-400">Wallet Deduction:</span>
                            <span className="text-green-400">-₹{walletAmount.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handlePaymentConfirm}
                        disabled={isProcessing}
                        className="btn-primary w-full py-3 text-base disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : `Pay with PayPal - $${(amountToPay / exchangeRate).toFixed(2)}`}
                      </button>
                      <p className="text-xs text-gray-500 text-center mt-3">
                        Secure payment powered by PayPal Business
                      </p>
                    </>
                  ) : (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-amber-500/20 rounded-lg">
                          <ClockIcon className="w-6 h-6 text-amber-400" />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-amber-400">PayPal - Coming Soon</h4>
                          <p className="text-xs text-amber-400/80">International payments via PayPal will be available soon</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-3">
                        We're working on adding PayPal support for international payments. In the meantime, please contact support@podstack.ai for assistance.
                      </p>
                      <button
                        disabled
                        className="btn-primary w-full py-3 text-base mt-4 opacity-50 cursor-not-allowed"
                      >
                        PayPal Coming Soon
                      </button>
                    </div>
                  )}
                </>
              )}

              {/* Crypto - Binance Pay */}
              {paymentRegion === 'crypto' && (
                <>
                  {isCryptoEnabled ? (
                    <>
                      <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4 mb-4">
                        <p className="text-sm text-purple-400">
                          Pay with cryptocurrency via Binance Pay. Supports USDT, BTC, ETH, and more.
                        </p>
                      </div>
                      <div className="mb-4 p-4 bg-gray-800 rounded-lg">
                        <div className="flex justify-between items-center mb-2">
                          <span className="text-sm text-gray-400">Amount to Pay:</span>
                          <span className="text-xl font-bold text-white">${(amountToPay / exchangeRate).toFixed(2)} USDT</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-gray-400">Equivalent to:</span>
                          <span className="text-gray-400">₹{amountToPay.toFixed(2)}</span>
                        </div>
                        {useWalletBalance && walletAmount > 0 && (
                          <div className="flex justify-between items-center text-sm mt-1">
                            <span className="text-green-400">Wallet Deduction:</span>
                            <span className="text-green-400">-₹{walletAmount.toFixed(2)}</span>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={handlePaymentConfirm}
                        disabled={isProcessing}
                        className="btn-primary w-full py-3 text-base disabled:opacity-50"
                      >
                        {isProcessing ? 'Processing...' : `Pay with Crypto - $${(amountToPay / exchangeRate).toFixed(2)}`}
                      </button>
                      <p className="text-xs text-gray-500 text-center mt-3">
                        Secure payment powered by Binance Pay
                      </p>
                    </>
                  ) : (
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-purple-500/20 rounded-lg">
                          <ClockIcon className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-purple-400">Crypto - Coming Soon</h4>
                          <p className="text-xs text-purple-400/80">Cryptocurrency payments will be available soon</p>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-3">
                        We're working on adding cryptocurrency payment support. In the meantime, please contact support@podstack.ai for assistance.
                      </p>
                      <button
                        disabled
                        className="btn-primary w-full py-3 text-base mt-4 opacity-50 cursor-not-allowed"
                      >
                        Crypto Coming Soon
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Checkout;
